import React, { useState, useEffect, useMemo } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { ToastProvider, useToast } from './Toast';
import { TranslationProvider } from './hooks/useTranslation';
import auditService from './auditService';
import apiClient from './apiClient';
import { lookupBySerialNo } from './prodOrderService';
import AppHeader from './components/AppHeader';
import QAOpsLoginScreen from './components/QAOpsLoginScreen';
import ChillerAuditDashboard from './components/ChillerAuditDashboard';
import UnitDetailsForm from './components/UnitDetailsForm';
import FormSelection from './components/FormSelectionPage';
import CategoryViewPage from './components/CategoryViewPage';
import ControlItemsViewPage from './components/ControlItemsViewPage';
import ChecklistPageLoop from './components/ChecklistPageLoop';
import SummaryItemPage from './components/SummaryItemPage';
import AuditSummaryPage from './components/AuditSummaryPage';
import QRScanner, { forceStopAllCameras } from './components/QRScanner';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen bg-[#F6F8FA] flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Main App Component (wrapped with AuthProvider)
function AppContent() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedForm, setSelectedForm] = useState(null);
  const [auditFormData, setAuditFormData] = useState(null);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  // ENHANCED NAVIGATION: Atomic context to prevent race conditions
  const [navigationContext, setNavigationContext] = useState({
    category: null,
    controlItem: null,
    timestamp: null
  });

  // Legacy support - derived from navigationContext
  const selectedCategory = navigationContext.category;
  const selectedControlItem = navigationContext.controlItem;
  const [formDataError, setFormDataError] = useState(null);
  const [currentAuditResponse, setCurrentAuditResponse] = useState(null);
  const [checklistData, setChecklistData] = useState({});
  const [dashboardStatistics, setDashboardStatistics] = useState(null);



  // NEW: Separate states for draft management
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [isCreatingNewAudit, setIsCreatingNewAudit] = useState(false);
  const [isResumedFromDraft, setIsResumedFromDraft] = useState(false); // Track if audit was resumed from dashboard
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);
  const [hasVisitedSummary, setHasVisitedSummary] = useState(false); // Track if user has visited summary page

  // Data loss prevention - warn users before leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Don't show warning on login page
      if (steps[currentStep] === 'login') {
        return;
      }

      // Show specific warning for audit-related steps
      const auditSteps = ['unitDetails', 'formSelect', 'categories', 'controlItems', 'checklist', 'auditSummary'];
      const isInAuditRegion = auditSteps.includes(steps[currentStep]);
      
      let message;
      if (isInAuditRegion) {
        message = 'You are currently in an audit session. Leaving this page may result in data loss. Please save your audit progress before leaving.';
      } else {
        message = 'You have unsaved changes. Are you sure you want to leave?';
      }

      // For modern browsers
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    const handlePopState = (e) => {
      // Don't show warning on login page
      if (steps[currentStep] === 'login') {
        return;
      }

      const auditSteps = ['unitDetails', 'formSelect', 'categories', 'controlItems', 'checklist', 'auditSummary'];
      const isInAuditRegion = auditSteps.includes(steps[currentStep]);
      
      if (isInAuditRegion) {
        const confirmLeave = window.confirm(
          'You are currently in an audit session. Going back may result in data loss. Please save your audit progress before leaving.\n\nDo you want to continue?'
        );
        
        if (!confirmLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname);
          e.preventDefault();
          return false;
        }
      } else {
        const confirmLeave = window.confirm(
          'You have unsaved changes. Are you sure you want to go back?'
        );
        
        if (!confirmLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname);
          e.preventDefault();
          return false;
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentStep]);

  // Fetch fresh audit response data when entering auditSummary step
  useEffect(() => {
    const fetchFreshDataForSummary = async () => {
      if (steps[currentStep] === 'auditSummary' && currentAuditResponse?.audit_form_response_id) {
        try {
          console.log('🔄 Fetching fresh audit response for summary...');
          const result = await auditService.getAuditResponse(currentAuditResponse.audit_form_response_id);
          if (result.success) {
            console.log('✅ Fresh data fetched for summary:', {
              hasCumulativeData: !!(result.auditResponse.raw_response_json?.cumulativeAuditData),
              cumulativeKeys: result.auditResponse.raw_response_json?.cumulativeAuditData ? 
                Object.keys(result.auditResponse.raw_response_json.cumulativeAuditData) : []
            });
            setCurrentAuditResponse(result.auditResponse);
          }
        } catch (error) {
          console.error('❌ Failed to fetch fresh data for summary:', error);
        }
      }
    };
    
    fetchFreshDataForSummary();
  }, [currentStep, currentAuditResponse?.audit_form_response_id]); // Run when step changes to auditSummary
  
  const [formData, setFormData] = useState({
    serialNumber: "",
    workOrder: "",
    unitModel: "",
    unitModelId: "",
    unitModelName: "",
    productionLine: "",
    productionLineId: "",
    productionLineName: "",
    dateTime: new Date().toLocaleString(),
    unitLock: false,
    inputMethod: "scan",
    productionOperatorName: "",
    auditNotes: ""
  });

  const steps = [
    'login',
    'dashboard',
    'cameraScan',  // New step for camera scanning
    'unitDetails',
    'formSelect',
    'categories',
    'controlItems',
    'checklist',
    'auditSummary',  // New prefinal submission preview
    'summary'
  ];

  // Update step based on authentication status
  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        // Only load drafts for dashboard display, don't auto-resume
        loadAvailableDrafts();
        if (currentStep === 0) {
          setCurrentStep(1);
        }
      } else {
        setCurrentStep(0);
        resetAuditState();
      }
    }
  }, [isAuthenticated, isLoading, currentStep]);

  // Load available drafts from backend API
  const loadAvailableDrafts = async () => {
  if (!user || !user.operator_profile?.operator_id) {
    console.log('No user or operator ID, skipping draft load');
    return;
  }
  
  try {
    console.log('Loading available drafts and statistics from backend...');
    
    // Use the updated method that includes statistics
    const result = await auditService.fetchAllDraftAudits(
      user.operator_profile.operator_id
    );
    
    console.log('Fetch result:', result);
    
    if (result.success && result.drafts) {
      setAvailableDrafts(result.drafts);
      setDashboardStatistics(result.statistics); // Store statistics
      console.log(`Found ${result.drafts.length} audits with statistics:`, result.statistics);
    } else {
      console.error('Failed to load drafts:', result.error);
      setAvailableDrafts([]);
      setDashboardStatistics(null);
    }
  } catch (error) {
    console.error('Error loading available drafts:', error);
    setAvailableDrafts([]);
    setDashboardStatistics(null);
  }
};

  // Refresh current audit response data from backend
  const refreshCurrentAuditResponse = async () => {
    if (!currentAuditResponse?.audit_form_response_id) return;
    
    console.log('🔄 Refreshing current audit response from backend...');
    
    try {
      const result = await auditService.getAuditResponse(
        currentAuditResponse.audit_form_response_id, 
        { useCache: false }
      );
      
      if (result.success) {
        console.log('✅ Refreshed audit response data');
        setCurrentAuditResponse(result.auditResponse);
      } else {
        console.error('❌ Failed to refresh audit response');
      }
    } catch (error) {
      console.error('❌ Error refreshing audit response:', error);
    }
  };

  // Reset all audit-related state
  const resetAuditState = () => {
    auditService.clearDraftData();
    setCurrentAuditResponse(null);
    setChecklistData({});
    setSelectedForm(null);
    setNavigationContext({ category: null, controlItem: null, timestamp: null });
    setAuditFormData(null);
    setIsCreatingNewAudit(false);
    setIsResumedFromDraft(false); // Reset resumed flag
    setFormData({
      serialNumber: "",
      workOrder: "",
      unitModel: "",
      unitModelId: "",
      unitModelName: "",
      productionLine: "",
      productionLineId: "",
      productionLineName: "",
      dateTime: new Date().toLocaleString(),
      unitLock: false,
      inputMethod: "scan"
    });
  };

  // PWA Installation and Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }

    let deferredPrompt;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
    });

    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
    });
  }, []);

  // Refresh audit response when returning to category/control items view
  const [lastRefreshStep, setLastRefreshStep] = useState(null);
  
  // Score state for real-time score tracking
  const [currentScore, setCurrentScore] = useState({ percentage: 0, achieved: 0, possible: 0 });
  
  // Sync score from currentAuditResponse
  useEffect(() => {
    if (currentAuditResponse?.score_percentage !== undefined) {
      setCurrentScore({
        percentage: Math.round(currentAuditResponse.score_percentage || 0),
        achieved: currentAuditResponse.total_points_achieved || 0,
        possible: currentAuditResponse.total_points_possible || 0
      });
    }
  }, [currentAuditResponse]);
  
  // Callback to update score from ChecklistPageLoop
  const handleScoreUpdate = (scoreData) => {
    setCurrentScore(scoreData);
  };
  
  useEffect(() => {
    const shouldRefresh = currentStep === 5 || currentStep === 6; // categories = 5, controlItems = 6
    const needsRefresh = shouldRefresh && currentAuditResponse && lastRefreshStep !== currentStep;
    
    if (needsRefresh) {
      setLastRefreshStep(currentStep);
      refreshCurrentAuditResponse();
    }
  }, [currentStep, currentAuditResponse?.audit_form_response_id]);

  // Fetch detailed audit form data
  const fetchAuditFormData = async (formId) => {
    try {
      setIsLoadingFormData(true);
      setFormDataError(null);
      
      console.log(`Fetching audit form data for ID: ${formId}`);
      
      const response = await apiClient.get(`/api/frontend/audit-forms/${formId}/`);

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched audit form data:', data);
        
        if (!data.success || !data.data) {
          throw new Error('Invalid response format from server');
        }
        
        setAuditFormData(data);
        return data;
      } else {
        if (response.status === 404) {
          throw new Error('Audit form not found');
        }
        throw new Error(`Failed to fetch audit form: ${response.status} ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Error fetching audit form:', error);
      setFormDataError(error.message);
      return null;
    } finally {
      setIsLoadingFormData(false);
    }
  };

  const handleLogin = (username, password) => {
    console.log('Login handled by AuthContext');
  };

  // ENHANCED: Start new audit with explicit flag and input method
  const handleStartNewAudit = (inputMethod = 'scan') => {
    console.log('Starting NEW audit (independent of drafts)...', inputMethod);
    
    // Clear any existing audit state
    resetAuditState();
    
    // Set flag to indicate we're creating a new audit
    setIsCreatingNewAudit(true);
    
    // Store input method in form data
    setFormData(prev => ({
      ...prev,
      inputMethod: inputMethod
    }));
    
    // Navigate based on input method
    if (inputMethod === 'scan') {
      // Go to camera scan first
      setCurrentStep(2); // cameraScan
    } else {
      // Go directly to unit details for manual entry
      setCurrentStep(3); // unitDetails
    }
  };

  // Handle barcode scan completion
  const handleScanComplete = (scannedSerialNumber) => {
    // Ensure camera is completely stopped before navigation
    forceStopAllCameras();

    // Update form data with scanned serial number
    setFormData(prev => ({
      ...prev,
      serialNumber: scannedSerialNumber,
      inputMethod: 'scan'
    }));

    // Auto-lookup production order from Business Central (non-blocking)
    lookupBySerialNo(scannedSerialNumber).then(info => {
      if (info) {
        setFormData(prev => ({
          ...prev,
          workOrder: info.prodOrderNo || prev.workOrder,
          unitModel: info.itemNo || prev.unitModel,
        }));
      }
    });

    // Small delay to ensure camera cleanup completes before navigation
    setTimeout(() => {
      // Navigate to unit details form
      setCurrentStep(3); // unitDetails
    }, 200);
  };

  // NEW: Resume existing draft
  const handleResumeDraft = async (draftAuditResponse) => {
    console.log('📋 RESUMING DRAFT AUDIT...');
    console.log('Draft response from dashboard:', draftAuditResponse);

    // Fetch the full audit response from backend to get complete data including cumulativeAuditData
    console.log('🔄 Fetching full audit response from backend...');
    const fullAuditResult = await auditService.getAuditResponse(
      draftAuditResponse.audit_form_response_id,
      { useCache: false }
    );

    if (!fullAuditResult.success) {
      console.error('❌ Failed to fetch full audit response');
      toast.error('Failed to load draft audit');
      return;
    }

    const fullAuditResponse = fullAuditResult.auditResponse;
    console.log('✅ Full audit response loaded:', {
      hasCumulativeData: !!fullAuditResponse.raw_response_json?.cumulativeAuditData,
      categoriesCount: Object.keys(fullAuditResponse.raw_response_json?.cumulativeAuditData || {}).length
    });

    setCurrentAuditResponse(fullAuditResponse);
    setIsCreatingNewAudit(false);
    setIsResumedFromDraft(true); // Mark that this audit was resumed from dashboard

    // Set up form data based on draft
    if (fullAuditResponse.audit_form) {
      setSelectedForm({
        id: `form_${fullAuditResponse.audit_form.audit_form_id}`,
        title: fullAuditResponse.audit_form.form_name,
        audit_form_id: fullAuditResponse.audit_form.audit_form_id
      });

      // Fetch the audit form data
      console.log('Loading form data for resumed draft...');
      await fetchAuditFormData(fullAuditResponse.audit_form.audit_form_id);
    }

    // Load any existing checklist data from draft
    if (fullAuditResponse.raw_response_json?.cumulativeAuditData) {
      const resumedData = fullAuditResponse.raw_response_json.cumulativeAuditData;
      console.log('✅ LOADING DRAFT DATA:', {
        categories: Object.keys(resumedData || {}).length,
        totalChecks: Object.values(resumedData || {}).reduce((sum, cat) =>
          sum + Object.values(cat.controlItems || {}).reduce((itemSum, item) =>
            itemSum + Object.keys(item.checks || {}).length, 0), 0),
        currentIndex: fullAuditResponse.raw_response_json.currentCategoryIndex,
        lastUpdated: fullAuditResponse.raw_response_json.lastUpdated
      });
      setChecklistData(resumedData);
    } else {
      console.log('⚠️ No cumulative audit data found in draft');
      setChecklistData({});
    }

    toast.auditResumed(fullAuditResponse.audit_form?.form_name || 'Unknown Form');

    // Go directly to categories view
    setCurrentStep(5);
  };

  const handleViewCompletedAudit = async (completedAudit) => {
    console.log('Viewing completed audit:', completedAudit);

    // Set the current audit response
    setCurrentAuditResponse(completedAudit);

    // Set up form data based on completed audit
    if (completedAudit.audit_form) {
      setSelectedForm({
        id: `form_${completedAudit.audit_form.audit_form_id}`,
        title: completedAudit.audit_form.form_name,
        form_name: completedAudit.audit_form.form_name,
        form_code: completedAudit.audit_form.form_code,
        audit_form_id: completedAudit.audit_form.audit_form_id
      });

      // Fetch the audit form data if needed
      if (!auditFormData || auditFormData.data?.audit_form_id !== completedAudit.audit_form.audit_form_id) {
        console.log('Loading form data for completed audit...');
        await fetchAuditFormData(completedAudit.audit_form.audit_form_id);
      }
    }

    // Set form data from the audit response
    setFormData({
      serialNumber: completedAudit.serial_number || "EWADH1617BSD2002",
      workOrder: completedAudit.work_order || "",
      unitModel: completedAudit.unit_model || "",
      productionLine: completedAudit.production_line || "",
      dateTime: completedAudit.submitted_at || completedAudit.created_at || new Date().toLocaleString(),
      unitLock: completedAudit.unit_lock || false
    });

    // Go directly to final summary view (SummaryItemPage) - step 9
    setCurrentStep(9);

    toast.info('Viewing completed audit');
  };

  const handleUnitDetailsSubmit = async () => {
    console.log('🔥 UNIT DETAILS SUBMIT CALLED!');
    console.log('🔥 currentAuditResponse exists:', !!currentAuditResponse);
    console.log('🔥 formData:', formData);
    
    // NOTE: At this point, no audit response exists yet (it's created when form is selected)
    // Unit details will be saved in handleFormSelection after audit response is created
    console.log('✅ Unit details stored in formData, will be saved after form selection');
    
    setCurrentStep(4); // formSelect
  };

  // ENHANCED: Form selection with independent audit creation
  const handleFormSelection = async (form) => {
    setSelectedForm(form);
    console.log('ENHANCED: Form selection for', isCreatingNewAudit ? 'NEW audit' : 'existing audit');
    console.log('Selected form:', form);
    
    try {
      // KEY CHANGE: Always create new audit when isCreatingNewAudit is true
      if (isCreatingNewAudit || !currentAuditResponse) {
        console.log('Creating completely new audit response...');
        
        const operatorId = user.user_type === 'OPERATOR' && user.operator_profile 
          ? user.operator_profile.operator_id 
          : null;
        
        const deviceInfo = auditService.getDeviceInfo();
        
        // Create brand new audit response
        const result = await auditService.createAuditResponse(
          form.audit_form_id,
          operatorId,
          deviceInfo.device_id,
          formData.shift || '',
          formData.workOrder || ''
        );
        
        if (result.success) {
          setCurrentAuditResponse(result.auditResponse);
          setChecklistData({}); // Start with empty checklist
          toast.auditStarted(form.title);
          console.log('New audit response created:', result.auditResponse.audit_form_response_id);
          
          // NOW save the unit details to the newly created audit response
          if (formData) {
            console.log('🔥 SAVING UNIT DETAILS TO NEW AUDIT RESPONSE');
            console.log('🔥 formData:', formData);
            
            try {
              const unitDetailsData = {
                serialNumber: formData.serialNumber || '',
                workOrder: formData.workOrder || '',
                unitModel: formData.unitModel || '',
                unitModelId: formData.unitModelId || '',
                productionLine: formData.productionLine || '',
                productionLineId: formData.productionLineId || '',
                inputMethod: formData.inputMethod || 'enter',
                updatedAt: new Date().toISOString()
              };
              
              // Update the audit response with unit details using dedicated fields
              const unitDetailsResult = await auditService.updateAuditResponse(
                result.auditResponse.audit_form_response_id,
                {
                  // FIXED: Use correct backend model field names from serializer line 174
                  unit_serial_number: formData.serialNumber || '',
                  unit_model: formData.unitModel || '',
                  unit_production_line: formData.productionLine || '',
                  unit_lock_status: formData.unitLock ? 'locked' : '',
                  unit_input_method: formData.inputMethod || 'enter',
                  // NEW: production operator + audit notes
                  production_operator_name: formData.productionOperatorName || '',
                  audit_notes: formData.auditNotes || '',
                  work_order: formData.workOrder || '',

                  // KEEP: Also preserve in raw_response_json for backward compatibility
                  raw_response_json: {
                    ...result.auditResponse.raw_response_json,
                    unitDetailsData: unitDetailsData,
                    // Also store at root level for easier access
                    serialNumber: formData.serialNumber || '',
                    workOrder: formData.workOrder || '',
                    unitModel: formData.unitModel || '',
                    productionLine: formData.productionLine || '',
                    productionOperatorName: formData.productionOperatorName || '',
                    auditNotes: formData.auditNotes || ''
                  }
                },
                { immediate: true }
              );
              
              if (unitDetailsResult.success) {
                console.log('✅ Unit details saved successfully to new audit response');
                console.log('🔍 DEBUG: Saved unit details:', unitDetailsData);
                console.log('🔍 DEBUG: Backend response:', unitDetailsResult.data);
                
                // Update the current audit response state with unit details
                const updatedAuditResponse = {
                  ...result.auditResponse,
                  raw_response_json: {
                    ...result.auditResponse.raw_response_json,
                    unitDetailsData: unitDetailsData,
                    serialNumber: formData.serialNumber || '',
                    workOrder: formData.workOrder || '',
                    unitModel: formData.unitModel || '',
                    productionLine: formData.productionLine || ''
                  }
                };
                
                console.log('🔍 DEBUG: Updated currentAuditResponse with unit details:', updatedAuditResponse);
                setCurrentAuditResponse(updatedAuditResponse);
              } else {
                console.error('❌ Failed to save unit details to new audit response:', unitDetailsResult.error);
                toast.error('Failed to save unit details');
              }
            } catch (error) {
              console.error('❌ Error saving unit details to new audit response:', error);
            }
          }
        } else {
          toast.error('Failed to start audit: ' + result.error);
          return;
        }
      } else {
        // Using existing audit response (resume draft scenario)
        console.log('Using existing audit response');
        toast.info('Continuing with existing audit');
      }
      
      // Fetch form data
      const formId = form.audit_form_id || form.id.replace('form_', '');
      await fetchAuditFormData(formId);
      
      // Reset the creating new audit flag
      setIsCreatingNewAudit(false);
      
      setCurrentStep(5);
    } catch (error) {
      console.error('Error in form selection:', error);
      toast.error('Failed to start audit');
    }
  };

  const handleCategoryClick = (category) => {
    // ATOMIC UPDATE: Set category navigation context
    setNavigationContext({
      category: category,
      controlItem: null, // Clear control item when selecting category
      timestamp: Date.now()
    });
    console.log('Selected category (atomic):', category);
    setCurrentStep(6);
  };

  const handleControlItemClick = async (controlItem, categoryInfo) => {
    console.log('🎯 ATOMIC NAVIGATION - Option 1 implementation');
    console.log('📋 Control item:', {
      id: controlItem.id,
      name: controlItem.name,
      translation: controlItem.translation,
      category_id: controlItem.originalControlItem?.category_id
    });
    console.log('📁 Category info provided:', categoryInfo);

    // STEP 1: Enhanced category context resolution with validation
    let resolvedCategory = null;

    // Priority 1: Use provided category info (most reliable)
    if (categoryInfo && categoryInfo.category_id) {
      resolvedCategory = {
        id: categoryInfo.category_id,
        name: categoryInfo.category_name,
        translation: categoryInfo.category_translation || categoryInfo.category_name,
        originalCategory: {
          categoryData: {
            category_id: categoryInfo.category_id,
            category_code: categoryInfo.category_code || categoryInfo.category_name
          }
        }
      };
      console.log('✅ Using provided category context:', resolvedCategory);
    }

    // Priority 2: Use current selected category if it matches
    else if (selectedCategory && controlItem.originalControlItem?.category_id === selectedCategory.id) {
      resolvedCategory = selectedCategory;
      console.log('✅ Using current selected category (matches control item):', resolvedCategory);
    }

    // Priority 3: Find category from audit form hierarchy
    else if (auditFormData?.hierarchy?.categories && controlItem.originalControlItem?.category_id) {
      const category = auditFormData.hierarchy.categories.find(
        cat => cat.category_id === controlItem.originalControlItem.category_id
      );
      if (category) {
        resolvedCategory = {
          id: category.category_id,
          name: category.category_name || category.category_code,
          translation: category.category_code_it || category.category_name || category.category_code,
          originalCategory: {
            categoryData: category
          }
        };
        console.log('✅ Found category from hierarchy:', resolvedCategory);
      }
    }

    // Priority 4: Try to find from form structure
    else if (auditFormData?.data?.form_structure && controlItem.originalControlItem?.category_id) {
      const categoryFromForm = auditFormData.data.form_structure.find(
        cat => cat.categoryData?.category_id === controlItem.originalControlItem.category_id
      );
      if (categoryFromForm) {
        resolvedCategory = {
          id: categoryFromForm.categoryData.category_id,
          name: categoryFromForm.categoryData.category_code,
          translation: categoryFromForm.categoryData.category_code_it || categoryFromForm.categoryData.category_code,
          originalCategory: categoryFromForm
        };
        console.log('✅ Found category from form structure:', resolvedCategory);
      }
    }

    // Priority 5: Fallback - create minimal category context
    if (!resolvedCategory) {
      const fallbackCategoryId = controlItem.originalControlItem?.category_id || 1;
      resolvedCategory = {
        id: fallbackCategoryId,
        name: `Category ${fallbackCategoryId}`,
        translation: `Category ${fallbackCategoryId}`,
        originalCategory: {
          categoryData: {
            category_id: fallbackCategoryId,
            category_code: `Category ${fallbackCategoryId}`
          }
        }
      };
      console.warn('⚠️ Using fallback category context:', resolvedCategory);
    }

    // STEP 2: Atomic navigation context update (prevents race conditions)
    const timestamp = Date.now();
    const atomicNavigationContext = {
      category: resolvedCategory,
      controlItem: controlItem,
      timestamp: timestamp
    };

    console.log('🔍 Pre-navigation validation:', {
      hasControlItem: !!controlItem,
      hasCategory: !!resolvedCategory,
      controlItemId: controlItem?.id,
      categoryId: resolvedCategory?.id,
      navigationReady: !!(controlItem && resolvedCategory),
      timestamp: timestamp
    });

    if (resolvedCategory && controlItem) {
      // ATOMIC UPDATE: Single state change with all navigation context
      setNavigationContext(atomicNavigationContext);
      console.log('✅ Atomic navigation context set successfully:', atomicNavigationContext);

      // STEP 3: Navigate to checklist immediately (no delay needed)
      console.log('🚀 Navigating to checklist with atomic context');
      setCurrentStep(7);
    } else {
      console.error('❌ Failed to resolve navigation context - cannot navigate');
    }
  };

  const handleStartFullAudit = (startingCategory = null) => {
    if (startingCategory) {
      setNavigationContext({
        category: startingCategory,
        controlItem: null,
        timestamp: Date.now()
      });
    }
    setCurrentStep(7);
  };

  // Enhanced save audit progress function for ChecklistPageLoop
  const saveAuditProgress = async (progressData = {}) => {
    // Check if progressData is actually a complete audit response object (from refresh)
    if (progressData.audit_form_response_id && progressData.status) {
      console.log('🔄 SAVE PROGRESS: Received complete audit response, updating state');
      setCurrentAuditResponse(progressData);
      return { success: true, auditResponse: progressData };
    }
    
    if (!currentAuditResponse || currentAuditResponse.status !== 'DRAFT') {
      console.log('Cannot save progress - no draft audit response');
      return;
    }
    
    try {
      console.log('🔍 SAVE PROGRESS: Current audit response raw_response_json keys:', 
        Object.keys(currentAuditResponse.raw_response_json || {}));
      console.log('🔍 SAVE PROGRESS: Has serialNumber?', 
        !!(currentAuditResponse.raw_response_json?.serialNumber));
      console.log('🔍 SAVE PROGRESS: Has unitDetailsData?', 
        !!(currentAuditResponse.raw_response_json?.unitDetailsData));
      
      const updateData = {
        raw_response_json: {
          // PRESERVE existing raw_response_json content (including unit details)
          ...currentAuditResponse.raw_response_json,
          // Then add/update with new progress data
          ...progressData,
          checklistData,
          currentStep,
          selectedCategory: selectedCategory?.id,
          selectedControlItem: selectedControlItem?.id,
          lastUpdated: new Date().toISOString()
        }
      };

      console.log('🔍 APP.JS SAVE PROGRESS DEBUG:', {
        progressDataKeys: progressData ? Object.keys(progressData) : [],
        hasCumulativeAuditData: !!(progressData?.cumulativeAuditData),
        cumulativeDataKeys: progressData?.cumulativeAuditData ? Object.keys(progressData.cumulativeAuditData) : [],
        finalUpdateDataKeys: Object.keys(updateData.raw_response_json),
        finalCumulativeData: updateData.raw_response_json.cumulativeAuditData
      });
      
      console.log('🔍 SAVE PROGRESS: Final updateData raw_response_json keys:', 
        Object.keys(updateData.raw_response_json || {}));
      console.log('🔍 SAVE PROGRESS: Final serialNumber?', 
        updateData.raw_response_json?.serialNumber);
      
      const result = await auditService.updateAuditResponse(
        currentAuditResponse.audit_form_response_id,
        updateData
      );
      
      console.log('🔍 SAVE RESULT DEBUG:', {
        hasResult: !!result,
        resultType: typeof result,
        resultKeys: result ? Object.keys(result) : [],
        hasSuccess: result?.success,
        hasAuditResponse: result?.auditResponse,
        fullResult: result
      });
      
      if (result && result.success) {
        // Handle immediate save with auditResponse
        if (result.auditResponse) {
          setCurrentAuditResponse(result.auditResponse);
        }
        // Handle queued saves (no auditResponse, but still successful)
        else if (result.queued) {
          console.log('✅ Save queued for batch processing');
        }
        return result;
      } else {
        console.error('❌ Save result invalid:', result);
        throw new Error(result?.error || 'Failed to save progress - invalid response');
      }
    } catch (error) {
      console.error('Error saving audit progress:', error);
      throw error;
    }
  };

  const handleChecklistItemChange = (categoryId, controlItemId, checkId, itemData) => {
    setChecklistData(prev => {
      const updated = {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          controlItems: {
            ...prev[categoryId]?.controlItems,
            [controlItemId]: {
              ...prev[categoryId]?.controlItems?.[controlItemId],
              checks: {
                ...prev[categoryId]?.controlItems?.[controlItemId]?.checks,
                [checkId]: {
                  ...itemData,
                  isCompleted: true,
                  lastUpdated: new Date().toISOString()
                }
              }
            }
          }
        }
      };
      
      return updated;
    });
  };

  // SOLUTION 1: Enhanced transformation function for App.js
  const transformCumulativeAuditDataToFieldResponses = (cumulativeData, auditFormData) => {
    const fieldResponses = [];
    
    if (!cumulativeData || !auditFormData) {
      console.log('Missing cumulative data or audit form data');
      return fieldResponses;
    }
    
    console.log('Starting enhanced transformation...');
    console.log('Input data:', { 
      cumulativeCategories: Object.keys(cumulativeData),
      auditFormStructure: auditFormData.hierarchy || auditFormData.data
    });
    
    // ENHANCED: Use multiple data sources for hierarchy
    let hierarchyData = null;
    
    // Try different hierarchy sources
    if (auditFormData.hierarchy) {
      hierarchyData = auditFormData.hierarchy;
    } else if (auditFormData.data?.hierarchy) {
      hierarchyData = auditFormData.data.hierarchy;
    } else if (auditFormData.data?.form_structure) {
      // Build hierarchy from form structure
      hierarchyData = buildHierarchyFromFormStructure(auditFormData.data.form_structure);
    }
    
    if (!hierarchyData) {
      console.log('No hierarchy data found');
      return fieldResponses;
    }
    
    console.log('Using hierarchy data:', {
      categories: hierarchyData.categories?.length || 0,
      controlItems: hierarchyData.control_items?.length || 0,
      checks: hierarchyData.checks?.length || 0
    });
    
    // Process each category in cumulative data
    Object.keys(cumulativeData).forEach(categoryId => {
      const categoryData = cumulativeData[categoryId];
      console.log(`Processing category ${categoryId}:`, categoryData);
      
      if (!categoryData.controlItems) {
        console.log(`No control items for category ${categoryId}`);
        return;
      }
      
      Object.keys(categoryData.controlItems).forEach(controlItemId => {
        const controlItemData = categoryData.controlItems[controlItemId];
        console.log(`Processing control item ${controlItemId}:`, controlItemData);
        
        if (!controlItemData.checks) {
          console.log(`No checks for control item ${controlItemId}`);
          return;
        }
        
        Object.keys(controlItemData.checks).forEach(checkId => {
          const checkData = controlItemData.checks[checkId];
          console.log(`Processing check ${checkId}:`, checkData);
          
          // CRITICAL: Only process FAILED checks (isChecked = true)
          if (!checkData.isChecked) {
            console.log(`Check ${checkId} passed, skipping`);
            return;
          }
          
          console.log(`Check ${checkId} FAILED, adding to field responses`);
          
          // ENHANCED: Find check details with multiple lookup strategies
          let checkDetails = null;
          
          // Strategy 1: Direct lookup in hierarchy
          if (hierarchyData.checks) {
            checkDetails = hierarchyData.checks.find(c => 
              c.check_id.toString() === checkId.toString()
            );
          }
          
          // Strategy 2: Use original check data from cumulative data
          if (!checkDetails && checkData.originalCheck) {
            checkDetails = checkData.originalCheck;
          }
          
          // Strategy 3: Build from form structure
          if (!checkDetails) {
            checkDetails = findCheckInFormStructure(auditFormData.data?.form_structure, checkId);
          }
          
          if (!checkDetails) {
            console.log(`No check details found for check ${checkId}, creating minimal entry`);
            checkDetails = {
              check_id: parseInt(checkId),
              check_code: `Check ${checkId}`,
              deduction_points: 0, // Default - no deduction for unknown checks
              measurement_unit: ''
            };
          }
          
          // ENHANCED: Find category and control item details
          const categoryDetails = findCategoryDetails(hierarchyData, categoryId, auditFormData.data?.form_structure);
          const controlItemDetails = findControlItemDetails(hierarchyData, controlItemId, auditFormData.data?.form_structure);
          
          // Calculate selected points
          const selectedPoints = checkData.isChecked ? (checkDetails.deduction_points || 0) : 0;
          
          
          // Create field response
          const fieldResponse = {
            field_builder_id: checkData.fieldBuilderId || null,
            category_id: categoryDetails?.category_id || parseInt(categoryId),
            control_item_id: controlItemDetails?.control_item_id || parseInt(controlItemId),
            check_id: checkDetails.check_id || parseInt(checkId),
            field_label: checkDetails.check_code || controlItemDetails?.control_item_code || `Check ${checkId}`,
            category_title: categoryDetails?.category_name || categoryData.categoryName || `Category ${categoryId}`,
            selected_points: selectedPoints,
            quantity: checkData.quantity || 1,
            remark: checkData.comment || '',
            production_phase: checkData.productionPhase || '',
            is_fixed_before_shipment: checkData.isFixedBeforeShipment || false,
            image_1_url: checkData.images?.[0]?.url || '',
            image_2_url: checkData.images?.[1]?.url || '',
            image_3_url: checkData.images?.[2]?.url || '',
            additional_data: {
              isChecked: checkData.isChecked,
              deductionPoints: checkDetails.deduction_points || 0,
              measurementUnit: checkDetails.measurement_unit || '',
              lastUpdated: checkData.lastUpdated || new Date().toISOString(),
              transformedAt: new Date().toISOString()
            }
          };
          
          fieldResponses.push(fieldResponse);
          console.log(`Added field response for failed check ${checkId}`);
        });
      });
    });
    
    console.log(`Transformation complete: ${fieldResponses.length} field responses generated`);
    return fieldResponses;
  };

  // HELPER FUNCTIONS
  function buildHierarchyFromFormStructure(formStructure) {
    if (!formStructure || !Array.isArray(formStructure)) return null;
    
    const categories = [];
    const controlItems = [];
    const checks = [];
    
    formStructure.forEach(category => {
      if (category.categoryData) {
        categories.push({
          category_id: category.categoryData.category_id,
          category_name: category.categoryData.category_code,
          category_code: category.categoryData.category_code
        });
      }
      
      if (category.fields) {
        category.fields.forEach(field => {
          if (field.controlItemData) {
            const controlItem = {
              control_item_id: field.controlItemData.control_item_id,
              control_item_code: field.controlItemData.control_item_code,
              control_item_code_it: field.controlItemData.control_item_code_it,
              category_id: field.controlItemData.category_id
            };
            controlItems.push(controlItem);
            
            // Create a check entry for this field
            const check = {
              check_id: field.id,
              check_code: field.label,
              check_code_it: field.description,
              control_item_id: field.controlItemData.control_item_id,
              deduction_points: field.pointOptions?.find(opt => opt.points > 0)?.points || 0,
              measurement_unit: ''
            };
            checks.push(check);
          }
        });
      }
    });
    
    return { categories, control_items: controlItems, checks };
  }

  function findCheckInFormStructure(formStructure, checkId) {
    if (!formStructure) return null;
    
    for (const category of formStructure) {
      if (category.fields) {
        for (const field of category.fields) {
          if (field.id.toString() === checkId.toString()) {
            return {
              check_id: field.id,
              check_code: field.label,
              check_code_it: field.description,
              deduction_points: field.pointOptions?.find(opt => opt.points > 0)?.points || 0,
              measurement_unit: ''
            };
          }
        }
      }
    }
    return null;
  }

  function findCategoryDetails(hierarchyData, categoryId, formStructure) {
    // Try hierarchy first
    if (hierarchyData?.categories) {
      const category = hierarchyData.categories.find(c => 
        c.category_id.toString() === categoryId.toString()
      );
      if (category) return category;
    }
    
    // Try form structure
    if (formStructure) {
      for (const category of formStructure) {
        if (category.categoryData?.category_id.toString() === categoryId.toString()) {
          return {
            category_id: category.categoryData.category_id,
            category_name: category.categoryData.category_code,
            category_code: category.categoryData.category_code
          };
        }
      }
    }
    
    return {
      category_id: parseInt(categoryId),
      category_name: `Category ${categoryId}`,
      category_code: `CAT_${categoryId}`
    };
  }

  function findControlItemDetails(hierarchyData, controlItemId, formStructure) {
    // Try hierarchy first
    if (hierarchyData?.control_items) {
      const controlItem = hierarchyData.control_items.find(ci => 
        ci.control_item_id.toString() === controlItemId.toString()
      );
      if (controlItem) return controlItem;
    }
    
    // Try form structure
    if (formStructure) {
      for (const category of formStructure) {
        if (category.fields) {
          for (const field of category.fields) {
            if (field.controlItemData?.control_item_id.toString() === controlItemId.toString()) {
              return {
                control_item_id: field.controlItemData.control_item_id,
                control_item_code: field.controlItemData.control_item_code,
                control_item_code_it: field.controlItemData.control_item_code_it
              };
            }
          }
        }
      }
    }
    
    return {
      control_item_id: parseInt(controlItemId),
      control_item_code: `Control Item ${controlItemId}`,
      control_item_code_it: `Control Item ${controlItemId}`
    };
  }

  // Final submission from audit summary page
  const handleFinalSubmitFromSummary = async () => {
    console.log('🎯 Final submission from audit summary page...');
    setIsSubmittingAudit(true);
    
    try {
      // Call the existing audit completion logic
      await handleAuditComplete();
      
      // If successful, navigate to the old/original summary page (step 9)
      console.log('✅ Audit submitted successfully, navigating to original summary page');
      toast.success('Audit submitted successfully!');
      
      // Navigate to the original summary page (final step)
      setCurrentStep(9); // This is the 'summary' step with SummaryItemPage
      
    } catch (error) {
      console.error('❌ Error during final submission:', error);
      toast.error('Failed to submit audit. Please try again.');
    } finally {
      setIsSubmittingAudit(false);
    }
  };

  const handleAuditComplete = async () => {
    console.log('ENHANCED: Starting audit completion process...');
    
    if (!currentAuditResponse || !auditFormData) {
      toast.error('Cannot complete audit - missing audit data');
      return;
    }
    
    try {
      console.log('Current audit response ID:', currentAuditResponse.audit_form_response_id);
      
      // Get the most up-to-date data with multiple fallback strategies
      let dataToTransform = null;
      let dataSource = 'unknown';
      
      // Strategy 1: Fresh server data
      console.log('Attempting to fetch fresh data from server...');
      try {
        const freshDataResult = await auditService.getAuditResponse(
          currentAuditResponse.audit_form_response_id
        );
        
        if (freshDataResult.success && 
            freshDataResult.auditResponse.raw_response_json?.cumulativeAuditData) {
          dataToTransform = freshDataResult.auditResponse.raw_response_json.cumulativeAuditData;
          dataSource = 'fresh_server_data';
          console.log('Using fresh server data');
        }
      } catch (error) {
        console.log('Could not fetch fresh data:', error.message);
      }
      
      // Strategy 2: Current response cumulative data
      if (!dataToTransform && 
          currentAuditResponse.raw_response_json?.cumulativeAuditData) {
        dataToTransform = currentAuditResponse.raw_response_json.cumulativeAuditData;
        dataSource = 'current_response_data';
        console.log('Using current response cumulative data');
      }
      
      // Strategy 3: Legacy checklist data
      if (!dataToTransform && checklistData && Object.keys(checklistData).length > 0) {
        dataToTransform = checklistData;
        dataSource = 'legacy_checklist_data';
        console.log('Using legacy checklist data');
      }
      
      // Validate data
      if (!dataToTransform || Object.keys(dataToTransform).length === 0) {
        console.error('CRITICAL: No data found for completion');
        toast.error('No audit data found. Please complete some checks before finishing.');
        return;
      }
      
      // Transform data
      console.log('Starting data transformation...');
      console.log('Data source:', dataSource);
      
      const fieldResponses = transformCumulativeAuditDataToFieldResponses(dataToTransform, auditFormData);
      
      console.log(`Final field responses count: ${fieldResponses.length}`);
      
      // Complete audit
      console.log('Submitting audit completion...');
      
      const completionRemarks = `Audit completed with ${fieldResponses.length} failed checks. Data source: ${dataSource}.`;
      
      const result = await auditService.completeAuditResponse(
        currentAuditResponse.audit_form_response_id,
        completionRemarks,
        fieldResponses
      );
      
      if (result.success) {
        console.log('✅ Audit completion successful!');
        console.log('📊 Completion result:', {
          status: result.auditResponse?.status,
          id: result.auditResponse?.audit_form_response_id,
          score: result.scoringSummary?.score_percentage
        });
        
        setCurrentAuditResponse(result.auditResponse);
        
        const finalScore = result.scoringSummary?.score_percentage || 0;
        
        toast.auditCompleted(Math.round(finalScore));
        setCurrentStep(8); // Go to summary (index 8 in steps array)
        
        // Update available drafts (remove completed audit)
        loadAvailableDrafts();
        
      } else {
        console.error('Audit completion failed:', result.error);
        toast.error(`Failed to complete audit: ${result.error}`);
      }
      
    } catch (error) {
      console.error('CRITICAL ERROR in audit completion:', error);
      toast.error(`Critical error during audit completion: ${error.message}`);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      // If we're on CategoryView (step 5) and moving to ControlItems or Checklist, set first category
      if (currentStep === 5 && auditFormData?.data?.form_structure?.length > 0) {
        // Sync checklistData with currentAuditResponse before navigating
        if (currentAuditResponse?.raw_response_json?.cumulativeAuditData) {
          const savedData = currentAuditResponse.raw_response_json.cumulativeAuditData;
          console.log('📊 handleNext: Syncing checklistData from currentAuditResponse:', {
            categoriesCount: Object.keys(savedData).length
          });
          setChecklistData(savedData);
        }

        const firstCategory = auditFormData.data.form_structure[0];
        setNavigationContext({
          category: {
            id: firstCategory.id || firstCategory.categoryData?.category_id,
            name: firstCategory.categoryData?.category_code || firstCategory.title.split(' / ')[0] || firstCategory.title,
            translation: firstCategory.categoryData?.category_code_it || firstCategory.title.split(' / ')[1] || "",
            originalCategory: firstCategory
          },
          controlItem: null,
          timestamp: Date.now()
        });
        console.log('📍 handleNext: Set first category for navigation');
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoBack = () => {
    // Sync checklistData with currentAuditResponse before going back
    if (currentAuditResponse?.raw_response_json?.cumulativeAuditData) {
      const savedData = currentAuditResponse.raw_response_json.cumulativeAuditData;
      console.log('📊 Syncing checklistData from currentAuditResponse:', {
        categoriesCount: Object.keys(savedData).length
      });
      setChecklistData(savedData);
    }
    setCurrentStep(6);
  };

  // ENHANCED: Complete with proper cleanup
  const handleComplete = () => {
    resetAuditState();
    loadAvailableDrafts(); // Refresh drafts list
    
    toast.success('Audit workflow completed successfully!');
    setCurrentStep(1); // Back to dashboard
  };

  const handleBackToDashboard = () => {
    // Force stop all cameras when going back to dashboard
    console.log('🛑 Forcing camera stop before going to dashboard');
    forceStopAllCameras();
    setCurrentStep(1);
  };

  // Navigation handlers with auto-save
  const handleChecklistNavigation = async (destination) => {
    // Force save any pending data before navigation
    try {
      // Force ChecklistPageLoop to save its internal state
      await auditService.forceSaveAll();

      // Wait a bit longer for queued saves to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh the currentAuditResponse to get latest saved data
      if (currentAuditResponse?.audit_form_response_id) {
        const result = await auditService.getAuditResponse(
          currentAuditResponse.audit_form_response_id,
          { useCache: false }
        );
        if (result.success) {
          setCurrentAuditResponse(result.auditResponse);
        }
      }
    } catch (error) {
      console.error('❌ NAVIGATION: Save failed:', error);
      // Continue with navigation even if save fails
    }

    switch (destination) {
      case 'controlItemsView':
        setCurrentStep(6);  // Navigate to controlItems (index 6)
        break;
      case 'categoryView':
        setCurrentStep(5);  // Navigate to categories (index 5)
        break;
      case 'summaryView':
      case 'auditSummary':
        setCurrentStep(8);  // Navigate to summary (index 8)
        break;
      case 'chillerAuditDashboard':
        setCurrentStep(1);  // Navigate to dashboard (index 1)
        break;
      default:
        console.log('Unknown navigation destination:', destination);
    }
  };

  const handleCategoryNavigation = (destination) => {
    switch (destination) {
      case 'controlItems':
        setCurrentStep(6);  // Navigate to controlItems (index 6)
        break;
      case 'checklist':
        setCurrentStep(7);  // Navigate to checklist (index 7)
        break;
      case 'startFullAudit':
        handleStartFullAudit();
        break;
      case 'summaryView':
      case 'auditSummary':
        setCurrentStep(8);  // Navigate to summary (index 8)
        break;
      case 'chillerAuditDashboard':
        setCurrentStep(1);  // Navigate to dashboard (index 1)
        break;
      default:
        console.log('Unknown navigation destination:', destination);
    }
  };

  const handleControlItemsNavigation = (destination) => {
    switch (destination) {
      case 'categoryView':
        setCurrentStep(5);  // Navigate to categories (index 5)
        break;
      case 'checklist':
        setCurrentStep(7);  // Navigate to checklist (index 7)
        break;
      case 'summaryView':
      case 'auditSummary':
        setCurrentStep(8);  // Navigate to summary (index 8)
        break;
      case 'chillerAuditDashboard':
        setCurrentStep(1);  // Navigate to dashboard (index 1)
        break;
      default:
        console.log('Unknown navigation destination:', destination);
    }
  };

  const handleSummaryNavigation = (destination) => {
    switch (destination) {
      case 'controlItemsView':
        setCurrentStep(5);
        break;
      case 'categoryView':
        setCurrentStep(4);
        break;
      case 'checklist':
        setCurrentStep(6);
        break;
      case 'chillerAuditDashboard':
        setCurrentStep(1);
        break;
      default:
        console.log('Unknown navigation destination:', destination);
    }
  };

  const navigateToStep = (stepName) => {
    const stepIndex = steps.indexOf(stepName);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  };

  // Get page title and subtitle for header
  const getPageInfo = () => {
    switch (steps[currentStep]) {
      case 'dashboard':
        return { 
          title: 'QA Operations Dashboard', 
          subtitle: 'Audit Management System' 
        };
      case 'unitDetails':
        return { 
          title: 'Unit Details', 
          subtitle: 'Enter unit information',
          showBackButton: true,
          onBack: handleBackToDashboard
        };
      case 'formSelect':
        return { 
          title: 'Form Selection', 
          subtitle: 'Choose audit form',
          showBackButton: true,
          onBack: handlePrevious
        };
      case 'categories':
        return { 
          title: 'Categories', 
          subtitle: selectedForm?.title || 'Audit Categories',
          showBackButton: true,
          onBack: handlePrevious
        };
      case 'controlItems':
        return { 
          title: 'Control Items', 
          subtitle: selectedCategory?.translation || 'Control Items',
          showBackButton: true,
          onBack: handlePrevious
        };
      case 'checklist':
        return { 
          title: 'Audit Checklist', 
          subtitle: selectedControlItem?.translation || 'Checklist Items',
          showBackButton: true,
          onBack: handlePrevious
        };
      case 'auditSummary':
        return { 
          title: 'Audit Review', 
          subtitle: 'Review before final submission',
          showBackButton: false
        };
      case 'summary':
        return { 
          title: 'Audit Summary', 
          subtitle: 'Review and complete audit',
          showBackButton: true,
          onBack: handleGoBack
        };
      default:
        return { title: '', subtitle: '' };
    }
  };

  // Calculate audit form progress based on completed categories/items
  const getAuditFormProgress = () => {
    if (!auditFormData?.data?.form_structure) return 0;
    
    const totalCategories = auditFormData.data.form_structure.length;
    if (totalCategories === 0) return 0;
    
    // Calculate progress based on current step and checklist completion
    let progress = 0;
    
    if (currentStep === 4) { // categories
      progress = 10; // Just started
    } else if (currentStep === 5) { // control items
      progress = 25; // Selected category
    } else if (currentStep === 6) { // checklist
      // Calculate based on completed checks
      const completedChecks = Object.values(checklistData).reduce((total, category) => {
        return total + Object.values(category.controlItems || {}).reduce((catTotal, controlItem) => {
          return catTotal + Object.values(controlItem.checks || {}).filter(check => check.isCompleted).length;
        }, 0);
      }, 0);
      
      const totalChecks = auditFormData.hierarchy?.checks?.length || 1;
      progress = 25 + (completedChecks / totalChecks) * 60; // 25% base + up to 60% for completion
    } else if (currentStep === 7) { // summary
      progress = 100; // Completed
    }
    
    return Math.min(Math.max(progress, 0), 100);
  };

  const renderCurrentStep = () => {
    const pageInfo = getPageInfo();
    
    switch (steps[currentStep]) {
      case 'login':
        return <QAOpsLoginScreen onLogin={handleLogin} />;
        
      case 'dashboard':
        return (
          <div className="min-h-screen bg-gray-50 pt-16 sm:pt-20">
            <AppHeader {...pageInfo} />
            <ChillerAuditDashboard 
              onStartNewAudit={handleStartNewAudit}
              user={user}
              availableDrafts={availableDrafts}
              onResumeDraft={handleResumeDraft}
              onViewCompletedAudit={handleViewCompletedAudit}
              isCreatingNewAudit={isCreatingNewAudit}
              statistics={dashboardStatistics}
            />
          </div>
        );
        
      case 'cameraScan':
        return (
          <div className="min-h-screen bg-gray-50">
            <QRScanner 
              onScanComplete={handleScanComplete}
              onBackToDashboard={handleBackToDashboard}
            />
          </div>
        );
        
      case 'unitDetails':
        return (
          <div className="min-h-screen bg-gray-50 pt-16 sm:pt-20">
            <AppHeader {...pageInfo} />
            <UnitDetailsForm 
              onSubmit={handleUnitDetailsSubmit}
              onBackToDashboard={handleBackToDashboard}
              formData={formData}
              setFormData={setFormData}
              user={user}
            />
          </div>
        );

      case 'formSelect':
        return (
          <div className="min-h-screen bg-gray-50">
            <AppHeader {...pageInfo} />
            <FormSelection
              onSelectForm={handleFormSelection}
              onPrevious={handlePrevious}
              selectedForm={selectedForm}
              user={user}
              // NEW: Pass flag to indicate this is for new audit
              isCreatingNewAudit={isCreatingNewAudit}
            />
          </div>
        );
        
      case 'categories':
        return (
          <div className="min-h-screen bg-gray-50">
            <AppHeader {...pageInfo} />
            <CategoryViewPage
              onPrevious={handlePrevious}
              onNext={handleNext}
              onCategoryClick={handleCategoryClick}
              onNavigate={handleCategoryNavigation}
              onStartFullAudit={handleStartFullAudit}
              navigateToStep={navigateToStep}
              selectedForm={selectedForm}
              selectedCategory={selectedCategory}
              auditFormData={auditFormData}
              isLoadingFormData={isLoadingFormData}
              formDataError={formDataError}
              currentAuditResponse={currentAuditResponse}
              isCreatingNewAudit={isCreatingNewAudit}
              isResumedFromDraft={isResumedFromDraft}
              user={user}
              currentScore={currentScore}
              showScore={currentAuditResponse && currentScore.percentage > 0}
            />
          </div>
        );

      case 'controlItems':
        return (
          <div className="min-h-screen bg-gray-50">
            <AppHeader {...pageInfo} />
            <ControlItemsViewPage
              onPrevious={handlePrevious}
              onNext={handleNext}
              onControlItemClick={handleControlItemClick}
              onNavigate={handleControlItemsNavigation}
              navigateToStep={navigateToStep}
              selectedForm={selectedForm}
              selectedCategory={selectedCategory}
              auditFormData={auditFormData}
              isLoadingFormData={isLoadingFormData}
              formDataError={formDataError}
              currentAuditResponse={currentAuditResponse}
              user={user}
              currentScore={currentScore}
              showScore={currentAuditResponse && currentScore.percentage > 0}
            />
          </div>
        );

      case 'checklist':
        return (
          <div className="min-h-screen bg-gray-50 pb-2">
            <AppHeader {...pageInfo} />
            <ChecklistPageLoop
              onNext={handleNext}
              onPrevious={handlePrevious}
              onNavigate={handleChecklistNavigation}
              onAuditComplete={handleAuditComplete}
              navigateToStep={navigateToStep}
              selectedForm={selectedForm}
              selectedCategory={selectedCategory}
              selectedControlItem={selectedControlItem}
              navigationContext={navigationContext}
              auditFormData={auditFormData}
              isLoadingFormData={isLoadingFormData}
              formDataError={formDataError}
              user={user}
              currentAuditResponse={currentAuditResponse}
              checklistData={checklistData}
              onChecklistItemChange={handleChecklistItemChange}
              onScoreUpdate={handleScoreUpdate}
              onSaveProgress={saveAuditProgress}
              showSummaryButton={hasVisitedSummary}
            />
          </div>
        );
        
      case 'auditSummary':
        // Mark that user has visited summary page
        if (!hasVisitedSummary) {
          setHasVisitedSummary(true);
        }

        // Debug the data being passed
        const cumulativeData = currentAuditResponse?.raw_response_json?.cumulativeAuditData || {};
        console.log('🔍 AUDIT SUMMARY DEBUG:', {
          hasCurrentAuditResponse: !!currentAuditResponse,
          hasRawResponseJson: !!currentAuditResponse?.raw_response_json,
          hasCumulativeAuditData: !!currentAuditResponse?.raw_response_json?.cumulativeAuditData,
          cumulativeDataKeys: Object.keys(cumulativeData),
          cumulativeDataCount: Object.keys(cumulativeData).length,
          checklistDataKeys: Object.keys(checklistData),
          checklistDataCount: Object.keys(checklistData).length,
          auditResponseId: currentAuditResponse?.audit_form_response_id
        });

        // Use checklistData as fallback if cumulativeAuditData is empty
        const dataToUse = Object.keys(cumulativeData).length > 0 ? cumulativeData : checklistData;

        // Callback to update "Fixed Before Shipment" flag
        const handleUpdateCheckFixedStatus = async (categoryId, controlItemId, checkId, isFixed) => {
          try {
            // Get current cumulative data - use checklistData as fallback if cumulativeAuditData is empty
            const currentCumulativeData = currentAuditResponse?.raw_response_json?.cumulativeAuditData || {};
            const sourceData = Object.keys(currentCumulativeData).length > 0 ? currentCumulativeData : checklistData;

            // Create updated data with deep copy
            const updatedCumulativeData = JSON.parse(JSON.stringify(sourceData));

            // Update the specific check
            if (updatedCumulativeData[categoryId]?.controlItems?.[controlItemId]?.checks?.[checkId]) {
              updatedCumulativeData[categoryId].controlItems[controlItemId].checks[checkId].isFixedBeforeShipment = isFixed;
            } else {
              console.error('Could not find check in data structure');
              return { success: false, error: 'Check not found' };
            }

            // Update backend
            const result = await auditService.updateAuditResponse(
              currentAuditResponse.audit_form_response_id,
              {
                raw_response_json: {
                  ...currentAuditResponse.raw_response_json,
                  cumulativeAuditData: updatedCumulativeData
                }
              }
            );

            if (result.success) {
              // Update local state immediately
              const updatedAuditResponse = {
                ...currentAuditResponse,
                raw_response_json: {
                  ...currentAuditResponse.raw_response_json,
                  cumulativeAuditData: updatedCumulativeData
                }
              };
              setCurrentAuditResponse(updatedAuditResponse);
            }

            return result;
          } catch (error) {
            console.error('Error updating fixed status:', error);
            return { success: false, error: error.message };
          }
        };

        return (
          <div className="min-h-screen bg-gray-50">
            <AuditSummaryPage
              auditFormData={auditFormData}
              currentAuditResponse={currentAuditResponse}
              cumulativeAuditData={dataToUse}
              currentScore={currentScore}
              selectedForm={selectedForm}
              onNavigateBack={() => setCurrentStep(5)} // Back to categories
              onNavigateToDashboard={() => setCurrentStep(1)} // Go to dashboard
              onUpdateCheckFixedStatus={handleUpdateCheckFixedStatus} // Update checkbox status
              onEditCategory={(category) => {
                // Navigate back to specific category
                setNavigationContext({
                  category: category,
                  controlItem: null,
                  timestamp: Date.now()
                });
                setCurrentStep(5);
              }}
              onEditControlItem={(categoryId, category, controlItem, controlItemId, checkItem) => {
                // Navigate to specific control item
                console.log('🔍 ============ onEditControlItem CALLED ============');
                console.log('🔍 categoryId:', categoryId);
                console.log('🔍 category:', category);
                console.log('🔍 controlItem:', controlItem);
                console.log('🔍 controlItemId:', controlItemId);
                console.log('🔍 checkItem:', checkItem);

                // Get the correct data source (same logic as in render)
                const cumulativeData = currentAuditResponse?.raw_response_json?.cumulativeAuditData || {};
                const dataSource = Object.keys(cumulativeData).length > 0 ? cumulativeData : checklistData;

                // Use categoryId to directly access the category data
                const categoryData = dataSource[categoryId] || category;

                console.log('🔍 Category lookup - ID:', categoryId);
                console.log('🔍 Category lookup - Found:', categoryData);
                console.log('🔍 Category lookup - categoryName:', categoryData?.categoryName);
                console.log('🔍 Category lookup - originalCategory:', categoryData?.originalCategory);

                if (checkItem) {
                  // Navigate to specific check item within checklist
                  console.log('📦 Navigating to specific check item');

                  console.log('📦 Found categoryData:', categoryData);

                  if (categoryData) {
                    setNavigationContext({
                      category: {
                        name: categoryData.categoryName,
                        translation: categoryData.categoryTranslation,
                        originalCategory: categoryData.originalCategory || categoryData, // Include full category data
                        id: categoryData.originalCategory?.categoryData?.category_id
                      },
                      controlItem: {
                        name: controlItem.controlItemName,
                        translation: controlItem.controlItemTranslation,
                        originalControlItem: controlItem.originalControlItem || controlItem, // Include full control item data
                        id: controlItem.originalControlItem?.control_item_id
                      },
                      timestamp: Date.now()
                    });
                    console.log('✅ Navigation context set for checklist');
                  }
                  setCurrentStep(7);
                } else {
                  // Navigate to control item checklist directly (when clicking "Edit" on control item card)
                  console.log('📦 Navigating to control item checklist');
                  console.log('📦 categoryData:', categoryData);
                  console.log('📦 controlItem FULL:', JSON.stringify(controlItem, null, 2));
                  console.log('📦 controlItem.controlItemName:', controlItem.controlItemName);
                  console.log('📦 controlItem.originalControlItem:', controlItem.originalControlItem);
                  console.log('📦 controlItem.originalControlItem?.control_item_id:', controlItem.originalControlItem?.control_item_id);

                  if (!categoryData) {
                    console.error('❌ Category data not found!');
                    return; // Exit early to prevent error
                  }

                  if (categoryData && controlItem) {
                    console.log('📦 NAVIGATION DEBUG:');
                    console.log('  controlItem.originalControlItem:', controlItem.originalControlItem);
                    console.log('  controlItemId from summary:', controlItemId);
                    console.log('  controlItem.originalControlItem?.control_item_id:', controlItem.originalControlItem?.control_item_id);

                    const resolvedControlItemId = controlItem.originalControlItem?.control_item_id || parseInt(controlItemId);

                    const controlItemContext = {
                      name: controlItem.controlItemName,
                      translation: controlItem.controlItemTranslation,
                      originalControlItem: controlItem.originalControlItem || controlItem,
                      id: resolvedControlItemId
                    };

                    console.log('  Final controlItem context:', controlItemContext);
                    console.log('  Final ID being used for navigation:', resolvedControlItemId);

                    setNavigationContext({
                      category: {
                        name: category.categoryName || categoryData.categoryName,
                        translation: category.categoryTranslation || categoryData.categoryTranslation,
                        originalCategory: category.originalCategory || categoryData.originalCategory || categoryData,
                        id: parseInt(categoryId)
                      },
                      controlItem: controlItemContext,
                      timestamp: Date.now()
                    });
                    console.log('✅ Navigation context set for control item edit');
                    setCurrentStep(7); // Go to checklist for this control item
                  } else if (categoryData) {
                    // Fallback: navigate to control items for that category
                    setNavigationContext({
                      category: {
                        name: categoryData.categoryName,
                        translation: categoryData.categoryTranslation,
                        originalCategory: categoryData.originalCategory || categoryData,
                        id: categoryData.originalCategory?.categoryData?.category_id || categoryId
                      },
                      controlItem: null,
                      timestamp: Date.now()
                    });
                    setCurrentStep(6);
                  }
                }
              }}
              onFinalSubmit={handleFinalSubmitFromSummary}
              isSubmitting={isSubmittingAudit}
            />
          </div>
        );
        
      case 'summary':
        // Check if viewing a completed audit (read-only mode)
        const isReadOnly = currentAuditResponse?.status === 'SUBMITTED';
        
        // Debug logging for summary page
        console.log('🔍 APP.JS SUMMARY CASE DEBUG:', {
          currentAuditResponse: currentAuditResponse ? {
            id: currentAuditResponse.audit_form_response_id,
            status: currentAuditResponse.status,
            hasData: !!currentAuditResponse
          } : null,
          isReadOnly,
          formData: formData ? { serialNumber: formData.serialNumber } : null
        });
        
        return (
          <div className="min-h-screen bg-gray-50 pb-2">
            <AppHeader {...pageInfo} />
            <SummaryItemPage 
              formData={formData}
              selectedForm={selectedForm}
              auditFormData={auditFormData}
              currentAuditResponse={currentAuditResponse}
              auditResponseId={currentAuditResponse?.audit_form_response_id}
              checklistData={checklistData}
              onGoBack={isReadOnly ? handleBackToDashboard : handleGoBack}
              onComplete={handleComplete}
              onNavigate={handleSummaryNavigation}
              navigateToStep={navigateToStep}
              user={user}
              isReadOnly={isReadOnly}
            />
          </div>
        );
        
      default:
        return <QAOpsLoginScreen onLogin={handleLogin} />;
    }
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="app">
      {/* PWA Meta Tags */}
      <div style={{ display: 'none' }}>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0098cb" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="QA Ops" />
        <meta name="description" content="QA Operations Scanning Tool" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="apple-touch-icon" href="/icon-192x192.png" />
      </div>

      {/* Audit Form Progress Indicator - Top */}
      {isAuthenticated && currentStep > 3 && auditFormData && (
        <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
          <div 
            className="h-full bg-green-500 transition-all duration-500 ease-out"
            style={{ width: `${getAuditFormProgress()}%` }}
          />
        </div>
      )}

      {/* App Content */}
      {renderCurrentStep()}
    </div>
  );
}

// Main PWA Application with AuthProvider and ToastProvider wrapper
export default function QAOpsPWA() {
  // Check if we're in barcode test mode
  
  
  return (
    <TranslationProvider>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
          {/* PWA Install Prompt - shows when app is installable */}
          <PWAInstallPrompt />
        </ToastProvider>
      </AuthProvider>
    </TranslationProvider>
  );
}